import ArrowDropDownRoundedIcon from "@mui/icons-material/ArrowDropDownRounded";
import { Button, ListItemIcon, ListItemText, Menu, MenuItem } from "@mui/material";
import { useState } from "react";
import type { JSX, ReactNode } from "react";

/**
 * A single action in the {@link ShareMenu} dropdown.
 */
export interface ShareMenuItem {
  key: string;
  label: string;
  /** Optional leading icon (e.g. an `@mui/icons-material` element). */
  icon?: ReactNode;
  onClick: () => void;
  /** `data-testid` for the menu item (e2e + unit). */
  testId?: string;
  disabled?: boolean;
}

export interface ShareMenuProps {
  items: ShareMenuItem[];
  /** Trigger button label. Default "Share". */
  label?: string;
  size?: "small" | "medium" | "large";
  /** Disable the trigger (e.g. while the entity is still loading). */
  disabled?: boolean;
}

/**
 * The shared **Share** button + dropdown used on every shareable detail page
 * (sales lead, CRM prospect, CRM customer). One button, one menu — so the
 * "Share via email / Share to chat / Copy link" pattern stays uniform across
 * the dashboard and the CRM sub-app. The page passes the menu items; this
 * component owns only the open/close state.
 *
 * Relative imports only (no `@/` alias) so it typechecks identically under the
 * dashboard tsconfig AND the CRM sub-app tsconfig (which remaps `@/`).
 */
export function ShareMenu({
  items,
  label = "Share",
  size = "small",
  disabled = false,
}: ShareMenuProps): JSX.Element {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const open = Boolean(anchor);

  return (
    <>
      <Button
        variant="contained"
        size={size}
        disabled={disabled}
        onClick={(e) => setAnchor(e.currentTarget)}
        endIcon={<ArrowDropDownRoundedIcon />}
        data-testid="share-menu-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {label}
      </Button>
      <Menu
        anchorEl={anchor}
        open={open}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {items.map((item) => (
          <MenuItem
            key={item.key}
            disabled={item.disabled ?? false}
            data-testid={item.testId}
            onClick={() => {
              setAnchor(null);
              item.onClick();
            }}
          >
            {item.icon ? <ListItemIcon>{item.icon}</ListItemIcon> : null}
            <ListItemText>{item.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
